import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Col, Form, Input, Row, Select, Switch } from 'antd';
import { useQuery } from '@common/hooks';
import { ErrorHandler, InfoTooltip, LoadingState } from '@common/components';
import { HealthDocTypes } from 'works/graphql/works-list-item-template';
import { FormHealthDocSelectMonthDay } from '@onboarding/components';
import ExpirationTypeSelect from './expiration-type-select';

export default function FormHealthDocumentTemplate({
  handleSubmit,
  initialValues,
  showSeason,
  showExpiration,
  form,
}) {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState(null);
  const [isSeasonal, setIsSeasonal] = useState(
    initialValues.seasonStartsAt && initialValues.seasonEndsAt
  );

  const { loading, error, data } = useQuery(HealthDocTypes);

  useEffect(() => {
    if (!data) return;
    setTypes(data.availableHealthDocs);

    if (!initialValues || !initialValues.healthDocId) return;
    const type = data.availableHealthDocs.find(
      (item) => item.id === initialValues.healthDocId
    );

    setSubtypes(type.categories);
  }, [data, initialValues]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="FormHealthDocumentTemplate" />;
  }

  const handleTypeSelect = (value) => {
    const type = data.availableHealthDocs.find((item) => item.id === value);
    form.setFieldsValue({
      healthDocSubtype: null,
      name: type.name,
    });
    setSubtypes(type.categories);
  };
  const seasonalTooltip = 'It means it will repeat yearly on the given dates.';

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Form.Item label="Active" name="active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item
        label="Health Document"
        name="healthDocId"
        rules={[{ required: true, message: 'Select a document type' }]}
      >
        <Select
          size="large"
          onSelect={handleTypeSelect}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {types.map(({ id, name }) => (
            <Select.Option key={id} value={id}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {subtypes && (
        <Form.Item
          label="Type"
          name="healthDocSubtype"
          rules={[{ required: true, message: 'Select a type' }]}
        >
          <Select size="large">
            {subtypes.map((value) => {
              return (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
      )}
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: 'Please enter a item name' }]}
      >
        <Input size="large" />
      </Form.Item>
      {showExpiration && <ExpirationTypeSelect form={form} />}
      <Form.Item label="Description" name="description">
        <Input.TextArea />
      </Form.Item>
      {showSeason && (
        <>
          <Row style={{ marginBottom: 16 }}>
            <Checkbox
              checked={isSeasonal}
              onChange={(event) => setIsSeasonal(event.target.checked)}
              className="is-seasonal-ant-checkbox"
            >
              Is this item seasonal?
              <InfoTooltip size="1x" title={seasonalTooltip} leftOffset={8} />
            </Checkbox>
          </Row>

          {isSeasonal && (
            <Row>
              <Col span={24}>
                <FormHealthDocSelectMonthDay
                  form={form}
                  initialValue={initialValues?.seasonStartsAt}
                  name="seasonStartsAt"
                  label="Start Date"
                  required={true}
                />
              </Col>
              <Col span={24}>
                <FormHealthDocSelectMonthDay
                  form={form}
                  initialValue={initialValues?.seasonEndsAt}
                  name="seasonEndsAt"
                  label="End Date"
                  required={true}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Form>
  );
}

FormHealthDocumentTemplate.propTypes = {
  initialValues: PropTypes.shape({
    active: PropTypes.bool,
    healthDocId: PropTypes.string,
    healthDocSubtype: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    seasonStartsAt: PropTypes.string,
    seasonEndsAt: PropTypes.string,
  }),
  showSeason: PropTypes.bool.isRequired,
  dismiss: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  showExpiration: PropTypes.bool,
};

FormHealthDocumentTemplate.defaultProps = {
  showExpiration: false,
};
